<script setup lang="ts">
  import { useTranslation } from '@/composables/useTranslation'
  import { useRouter, useRoute } from 'vue-router'
  import { ref, computed, onMounted } from 'vue'
  import { ArrowLeftIcon, ChevronRightIcon, PlusIcon, TrashIcon } from '@heroicons/vue/24/outline'
  import { Button } from '@/components/ui/button'
  import { Badge } from '@/components/ui/badge'
  import { Switch } from '@/components/ui/switch'
  import { Card, CardContent } from '@/components/ui/card'
  import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
  import { Input } from '@/components/ui/input'
  import { Textarea } from '@/components/ui/textarea'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import { PhoneForwardingRulesService } from '@/types/generated/services.gen'
  import type { PhoneForwardingRuleRequest, RuleTypeEnum } from '@/types/generated/types.gen'
  import { useToast } from '@/components/ui/toast'
  import { usePhoneNumbers } from '@/composables/usePhoneNumbers'
  import { yup } from '@/lib/validation'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'

  const { t } = useTranslation({
    en: {
      newRule: 'New Rule',
      editRule: 'Edit Rule',
      backToRulesList: 'Back to rules list',
      setCallForwardingRule: 'Set a call forwarding rule',
      ensureImportantCallers: 'Ensure important callers reach your team without delay.',
      selectCondition: 'Select Condition',
      basedOnPhoneNumbers: 'Based on phone numbers',
      basedOnEntityTypes: 'Based on entity types',
      basedOnTextContext: 'Based on Text Context',
      whenCallComesFrom: 'When a call comes from one of these numbers:',
      whenCallerIdentifiesAs: 'When the caller identifies as:',
      whenConditionIsMet: 'When the condition is met:',
      itMustBeForwardedTo: 'It must be forwarded to:',
      messageBeforeForwarding: 'Message Before Forwarding',
      newNumber: 'New number',
      newEntity: 'New entity',
      selectEntity: 'Select the entity',
      reviewCallForwardingRule: 'Review the call forwarding rule',
      pleaseCheckDetails: 'Please check the details below before saving your rule.',
      ruleBasedOnEntityTypes: 'Rule based on entity types',
      active: 'Active',
      cancel: 'Cancel',
      next: 'Next',
      back: 'Back',
      save: 'Save',
      connectingToAttorney: "I'll forward you to someone who can help with your case.",
      noForwardingMessage: 'No forwarding message, please set a message',
      ruleTitle: 'Rule Title',
      untitledRule: 'Untitled Rule',
      enterRuleTitle: 'Enter rule title',
      errorLoadingRule: 'Error loading rule',
      errorSavingRule: 'Error saving rule',
      ruleSaved: 'Rule saved successfully',
      ruleUpdated: 'Rule updated successfully',
      invalidPhoneNumber: 'Please enter a valid phone number',
      phoneNumberRequired: 'Phone number is required',
      phoneNumberAlreadyExists: 'This phone number already exists',
      applyToPhoneNumbers: 'Apply to Phone Numbers',
      loadingPhoneNumbers: 'Loading phone numbers...',
      noPhoneNumbersAvailable: 'No phone numbers available',
      inactive: 'Inactive',
      appliedToPhoneNumbers: 'Applied to phone numbers',
      judge: 'Judge',
      court: 'Court',
      witness: 'Witness',
      lawyer: 'Lawyer',
      prosecutor: 'Prosecutor',
      phoneNumberSelectionRequired: 'Please select at least one phone number',
      forwardTo: 'Forward to {phoneNumber}',
      enterKeywordsOrPhrases: 'Enter multiple keywords or phrases... For example: "I am in prison".'
    },
    de: {
      newRule: 'Neue Regel',
      editRule: 'Regel bearbeiten',
      backToRulesList: 'Zurück zur Regelliste',
      setCallForwardingRule: 'Anrufweiterleitungsregel festlegen',
      ensureImportantCallers:
        'Stellen Sie sicher, dass wichtige Anrufer Ihr Team ohne Verzögerung erreichen.',
      selectCondition: 'Bedingung auswählen',
      basedOnPhoneNumbers: 'Basierend auf Telefonnummern',
      basedOnEntityTypes: 'Basierend auf Entitätstypen',
      basedOnTextContext: 'Basierend auf Textkontext',
      whenCallComesFrom: 'Wenn ein Anruf von einer dieser Nummern kommt:',
      whenCallerIdentifiesAs: 'Wenn sich der Anrufer identifiziert als:',
      whenConditionIsMet: 'Wenn die Bedingung erfüllt ist:',
      itMustBeForwardedTo: 'Soll dieser Anruf weitergeleitet werden an:',
      messageBeforeForwarding: 'Nachricht vor der Weiterleitung',
      newNumber: 'Neue Nummer',
      newEntity: 'Neue Entität',
      selectEntity: 'Entität auswählen',
      reviewCallForwardingRule: 'Anrufweiterleitungsregel überprüfen',
      pleaseCheckDetails: 'Bitte überprüfen Sie die Details unten, bevor Sie Ihre Regel speichern.',
      ruleBasedOnEntityTypes: 'Regel basierend auf Entitätstypen',
      active: 'Aktiv',
      cancel: 'Abbrechen',
      next: 'Weiter',
      back: 'Zurück',
      save: 'Speichern',
      connectingToAttorney:
        'Ich leite Sie an jemanden weiter, der Ihnen bei Ihrem Fall helfen kann.',
      noForwardingMessage: 'Keine Nachricht vor der Weiterleitung, bitte setzen Sie eine Nachricht',
      ruleTitle: 'Regel-Titel',
      untitledRule: 'Unbenannte Regel',
      enterRuleTitle: 'Regel-Titel eingeben',
      errorLoadingRule: 'Fehler beim Laden der Regel',
      errorSavingRule: 'Fehler beim Speichern der Regel',
      ruleSaved: 'Regel erfolgreich gespeichert',
      ruleUpdated: 'Regel erfolgreich aktualisiert',
      invalidPhoneNumber: 'Bitte geben Sie eine gültige Telefonnummer ein',
      phoneNumberRequired: 'Telefonnummer ist erforderlich',
      phoneNumberAlreadyExists: 'Diese Telefonnummer existiert bereits',
      applyToPhoneNumbers: 'Auf Telefonnummern anwenden',
      loadingPhoneNumbers: 'Telefonnummern werden geladen...',
      noPhoneNumbersAvailable: 'Keine Telefonnummern verfügbar',
      inactive: 'Inaktiv',
      appliedToPhoneNumbers: 'Angewendet auf die Telefonnummern',
      judge: 'Richter',
      court: 'Gericht',
      witness: 'Zeuge',
      lawyer: 'Anwalt',
      prosecutor: 'Staatsanwalt',
      phoneNumberSelectionRequired: 'Bitte wählen Sie mindestens eine Telefonnummer aus',
      forwardTo: 'Weiterleiten an {phoneNumber}',
      enterKeywordsOrPhrases:
        'Mehrere Schlüsselwörter oder Phrasen eingeben. Zum Beispiel: "Ich sitze grade im Gefängnis".'
    },
    fr: {
      newRule: 'Nouvelle règle',
      editRule: 'Modifier la règle',
      backToRulesList: 'Retour à la liste des règles',
      setCallForwardingRule: "Définir une règle de transfert d'appels",
      ensureImportantCallers:
        'Permettez aux appelants importants de joindre votre équipe sans délai.',
      selectCondition: 'Sélectionner une condition',
      basedOnPhoneNumbers: 'Selon les numéros de téléphone',
      basedOnEntityTypes: "Selon les types d'entités",
      basedOnTextContext: 'Selon le contexte textuel',
      whenCallComesFrom: "Lorsqu'un appel provient de l'un de ces numéros :",
      whenCallerIdentifiesAs: "Lorsque l'appelant est identifié comme :",
      whenConditionIsMet: 'Lorsque la condition est remplie :',
      itMustBeForwardedTo: "L'appel doit être transféré vers :",
      messageBeforeForwarding: 'Message avant le transfert',
      newNumber: 'Nouveau numéro',
      newEntity: 'Nouvelle entité',
      selectEntity: 'Sélectionner une entité',
      reviewCallForwardingRule: 'Vérifier la règle de transfert',
      pleaseCheckDetails: "Vérifiez les informations ci-dessous avant d'enregistrer la règle",
      ruleBasedOnEntityTypes: "Règle selon les types d'entités",
      active: 'Actif',
      cancel: 'Annuler',
      next: 'Suivant',
      back: 'Précédent',
      save: 'Enregistrer',
      connectingToAttorney: 'Je vous mets en relation avec une personne qui peut vous aider.',
      noForwardingMessage: 'Aucun message de transfert défini. Veuillez en ajouter un.',
      ruleTitle: 'Titre de la règle',
      untitledRule: 'Règle sans titre',
      enterRuleTitle: 'Saisissez le titre de la règle',
      errorLoadingRule: 'Erreur lors du chargement de la règle',
      errorSavingRule: "Erreur lors de l'enregistrement de la règle",
      ruleSaved: 'La règle a été enregistrée avec succès',
      ruleUpdated: 'La règle a été mise à jour avec succès',
      invalidPhoneNumber: 'Veuillez saisir un numéro de téléphone valide',
      phoneNumberRequired: 'Le numéro de téléphone est obligatoire',
      phoneNumberAlreadyExists: 'Ce numéro de téléphone est déjà utilisé',
      applyToPhoneNumbers: 'Appliquer aux numéros de téléphone',
      loadingPhoneNumbers: 'Chargement des numéros de téléphone…',
      noPhoneNumbersAvailable: 'Aucun numéro de téléphone disponible',
      inactive: 'Inactif',
      appliedToPhoneNumbers: 'Appliqué aux numéros de téléphone',
      judge: 'Juge',
      court: 'Tribunal',
      witness: 'Témoin',
      lawyer: 'Avocat',
      prosecutor: 'Procureur',
      phoneNumberSelectionRequired: 'Veuillez sélectionner au moins un numéro de téléphone',
      forwardTo: 'Transférer vers {phoneNumber}',
      enterKeywordsOrPhrases:
        'Saisissez plusieurs mots-clés ou expressions… Par exemple : "Je suis en prison"'
    }
  })

  const router = useRouter()
  const route = useRoute()
  const { toast } = useToast()
  const { trackEvent } = useMetrics()

  const currentStep = ref(1)
  const loading = ref(false)
  const ruleType = ref<RuleTypeEnum>('phonenumber')
  const isActive = ref(true)
  const ruleTitle = ref('')
  const targetPhoneNumber = ref('')
  const forwardingMessage = ref('')

  const {
    phoneNumbers: availablePhoneNumbers,
    loading: loadingPhoneNumbers,
    loadPhoneNumbers,
    getPhoneNumberDisplay
  } = usePhoneNumbers()
  const selectedPhoneNumberUuids = ref<string[]>([])

  const phoneNumbers = ref<string[]>([])
  const newPhoneNumber = ref('')

  const phoneNumberError = ref('')
  const isPhoneNumberValid = ref(false)

  // Target phone number validation state
  const targetPhoneNumberError = ref('')
  const isTargetPhoneNumberValid = ref(false)

  // Phone number selection validation state
  const phoneNumberSelectionError = ref('')

  // For entity types rule type
  const selectedEntities = ref<string[]>([])
  const newEntitySelection = ref<string>('')
  const availableEntities = computed(() => [
    { value: 'judge', title: t('judge') },
    { value: 'court', title: t('court') },
    { value: 'witness', title: t('witness') },
    { value: 'lawyer', title: t('lawyer') },
    { value: 'prosecutor', title: t('prosecutor') }
  ])

  // For text context rule type
  const textContext = ref('')

  const isEditing = computed(() => route.params.uuid && route.params.uuid !== 'new')

  const loadRule = async () => {
    if (!isEditing.value) return

    loading.value = true
    try {
      const rule = await PhoneForwardingRulesService.phoneForwardingRulesRetrieve({
        uuid: route.params.uuid as string
      })

      ruleType.value = rule.rule_type
      isActive.value = rule.active ?? true
      ruleTitle.value = rule.title || ''
      targetPhoneNumber.value = rule.target_phone_number || ''
      forwardingMessage.value = rule.forwarding_message || t('connectingToAttorney')
      if (rule.rule_type === 'phonenumber' && rule.from_numbers) {
        phoneNumbers.value = Array.isArray(rule.from_numbers) ? rule.from_numbers : []
      } else if (rule.rule_type === 'entity' && rule.from_entity_types) {
        if (Array.isArray(rule.from_entity_types) && rule.from_entity_types.length > 0) {
          if (typeof rule.from_entity_types[0] === 'object') {
            selectedEntities.value = rule.from_entity_types.map((entity: any) => {
              const entityInfo = availableEntities.value.find((e) => e.title === entity.title)
              return entityInfo ? entityInfo.value : entity.title.toLowerCase().replace(' ', '_')
            })
          } else {
            selectedEntities.value = rule.from_entity_types
          }
        } else {
          selectedEntities.value = []
        }
      } else if (rule.rule_type === 'text' && rule.from_text_context) {
        textContext.value = rule.from_text_context
      }

      // Load associated phone number UUIDs
      if (rule.phone_number_uuids) {
        selectedPhoneNumberUuids.value = rule.phone_number_uuids
      }
    } catch (error) {
      console.error('Error loading rule:', error)
      toast({
        title: t('errorLoadingRule'),
        variant: 'destructive'
      })
    } finally {
      loading.value = false
    }
  }

  const goBack = () => {
    if (currentStep.value > 1) {
      currentStep.value--
    } else {
      router.back()
    }
  }

  const goNext = () => {
    // Auto-add phone number if it's valid but not added yet
    if (
      ruleType.value === 'phonenumber' &&
      newPhoneNumber.value.trim() &&
      isPhoneNumberValid.value
    ) {
      addPhoneNumber()
    }

    // Auto-add entity if it's selected but not added yet
    if (
      ruleType.value === 'entity' &&
      newEntitySelection.value &&
      !selectedEntities.value.includes(newEntitySelection.value)
    ) {
      addEntity()
    }

    // Validate phone number selection and show error if none selected
    validatePhoneNumberSelection()

    if (selectedPhoneNumberUuids.value.length === 0) {
      return
    }

    if (currentStep.value < 2) {
      currentStep.value++
    }
  }

  const getAutoTitle = () => {
    if (ruleTitle.value.trim()) {
      return ruleTitle.value
    }
    return t('forwardTo', { phoneNumber: targetPhoneNumber.value })
  }

  const saveRule = async () => {
    loading.value = true
    try {
      const requestBody: PhoneForwardingRuleRequest = {
        title: getAutoTitle(),
        rule_type: ruleType.value,
        active: isActive.value,
        target_phone_number: targetPhoneNumber.value,
        forwarding_message: forwardingMessage.value,
        from_numbers: ruleType.value === 'phonenumber' ? phoneNumbers.value : undefined,
        from_entity_types: ruleType.value === 'entity' ? selectedEntities.value : undefined,
        from_text_context: ruleType.value === 'text' ? textContext.value : undefined,
        phone_number_uuids: selectedPhoneNumberUuids.value
      }

      if (isEditing.value) {
        await PhoneForwardingRulesService.phoneForwardingRulesUpdate({
          uuid: route.params.uuid as string,
          requestBody
        })

        // Track phone forwarding rule updated event
        trackEvent(MetricEventType.PHONE_FORWARDING_RULE_UPDATED, {
          rule_type: ruleType.value,
          has_phone_numbers: ruleType.value === 'phonenumber' && phoneNumbers.value.length > 0,
          phone_numbers_count: ruleType.value === 'phonenumber' ? phoneNumbers.value.length : 0,
          has_entity_types: ruleType.value === 'entity' && selectedEntities.value.length > 0,
          entity_types_count: ruleType.value === 'entity' ? selectedEntities.value.length : 0,
          entity_types: ruleType.value === 'entity' ? selectedEntities.value : undefined,
          has_text_context: ruleType.value === 'text' && !!textContext.value,
          is_active: isActive.value,
          has_forwarding_message: !!forwardingMessage.value,
          phone_numbers_associated_count: selectedPhoneNumberUuids.value.length
        })

        toast({
          title: t('ruleUpdated')
        })
      } else {
        await PhoneForwardingRulesService.phoneForwardingRulesCreate({
          requestBody
        })

        // Track phone forwarding rule created event
        trackEvent(MetricEventType.PHONE_FORWARDING_RULE_CREATED, {
          rule_type: ruleType.value,
          has_phone_numbers: ruleType.value === 'phonenumber' && phoneNumbers.value.length > 0,
          phone_numbers_count: ruleType.value === 'phonenumber' ? phoneNumbers.value.length : 0,
          has_entity_types: ruleType.value === 'entity' && selectedEntities.value.length > 0,
          entity_types_count: ruleType.value === 'entity' ? selectedEntities.value.length : 0,
          entity_types: ruleType.value === 'entity' ? selectedEntities.value : undefined,
          has_text_context: ruleType.value === 'text' && !!textContext.value,
          is_active: isActive.value,
          has_forwarding_message: !!forwardingMessage.value,
          phone_numbers_associated_count: selectedPhoneNumberUuids.value.length
        })

        toast({
          title: t('ruleSaved')
        })
      }

      router.back()
    } catch (error) {
      console.error('Error saving rule:', error)
      toast({
        title: t('errorSavingRule'),
        variant: 'destructive'
      })
    } finally {
      loading.value = false
    }
  }

  const validateFromPhoneNumber = (phoneNumber: string): boolean => {
    if (!phoneNumber.trim() && phoneNumbers.value.length === 0) {
      phoneNumberError.value = t('phoneNumberRequired')
      isPhoneNumberValid.value = false
      return false
    }

    try {
      const phoneSchema = (yup.string() as any).phone(t('invalidPhoneNumber'))
      phoneSchema.validateSync(phoneNumber)

      if (phoneNumbers.value.includes(phoneNumber)) {
        phoneNumberError.value = t('phoneNumberAlreadyExists')
        isPhoneNumberValid.value = false
        return false
      }

      phoneNumberError.value = ''
      isPhoneNumberValid.value = true
      return true
    } catch (error) {
      if (error instanceof Error) {
        phoneNumberError.value = error.message
      } else {
        phoneNumberError.value = t('invalidPhoneNumber')
      }
      isPhoneNumberValid.value = false
      return false
    }
  }

  const validateTargetPhoneNumber = (phoneNumber: string): boolean => {
    if (!phoneNumber.trim()) {
      targetPhoneNumberError.value = t('phoneNumberRequired')
      isTargetPhoneNumberValid.value = false
      return false
    }

    try {
      const phoneSchema = (yup.string() as any).phone(t('invalidPhoneNumber'))
      phoneSchema.validateSync(phoneNumber)

      targetPhoneNumberError.value = ''
      isTargetPhoneNumberValid.value = true
      return true
    } catch (error) {
      if (error instanceof Error) {
        targetPhoneNumberError.value = error.message
      } else {
        targetPhoneNumberError.value = t('invalidPhoneNumber')
      }
      isTargetPhoneNumberValid.value = false
      return false
    }
  }

  const addPhoneNumber = () => {
    if (validateFromPhoneNumber(newPhoneNumber.value)) {
      phoneNumbers.value.push(newPhoneNumber.value)
      newPhoneNumber.value = ''
      phoneNumberError.value = ''
      isPhoneNumberValid.value = false
    }
  }

  const removePhoneNumber = (index: number) => {
    phoneNumbers.value.splice(index, 1)
  }

  const addEntity = () => {
    if (newEntitySelection.value && !selectedEntities.value.includes(newEntitySelection.value)) {
      selectedEntities.value.push(newEntitySelection.value)
      newEntitySelection.value = ''
    }
  }

  const removeEntity = (entityValue: string) => {
    const index = selectedEntities.value.indexOf(entityValue)
    if (index > -1) {
      selectedEntities.value.splice(index, 1)
    }
  }

  const getEntityTitle = (entityValue: string) => {
    const entity = availableEntities.value.find((e) => e.value === entityValue)
    return entity ? entity.title : entityValue
  }

  const getConditionLabel = computed(() => {
    switch (ruleType.value) {
      case 'phonenumber':
        return t('whenCallComesFrom')
      case 'entity':
        return t('whenCallerIdentifiesAs')
      case 'text':
        return t('whenConditionIsMet')
      default:
        return t('whenCallerIdentifiesAs')
    }
  })

  const isFormValid = computed(() => {
    if (!isTargetPhoneNumberValid.value) {
      return false
    }

    if (phoneNumberSelectionError.value) {
      return false
    }

    if (
      ruleType.value === 'phonenumber' &&
      phoneNumbers.value.length === 0 &&
      !(newPhoneNumber.value.trim() && isPhoneNumberValid.value)
    ) {
      return false
    }

    if (
      ruleType.value === 'entity' &&
      selectedEntities.value.length === 0 &&
      !newEntitySelection.value
    ) {
      return false
    }

    if (ruleType.value === 'text' && !textContext.value.trim()) {
      return false
    }

    return true
  })

  const toggleRuleStatus = (checked: boolean) => {
    isActive.value = checked
  }

  const validatePhoneNumberSelection = () => {
    if (selectedPhoneNumberUuids.value.length === 0) {
      phoneNumberSelectionError.value = t('phoneNumberSelectionRequired')
    } else {
      phoneNumberSelectionError.value = ''
    }
  }

  const togglePhoneNumber = (uuid: string) => {
    const index = selectedPhoneNumberUuids.value.indexOf(uuid)
    if (index > -1) {
      selectedPhoneNumberUuids.value.splice(index, 1)
    } else {
      selectedPhoneNumberUuids.value.push(uuid)
    }
    validatePhoneNumberSelection()
  }

  onMounted(() => {
    loadPhoneNumbers()

    if (!isEditing.value && route.query['rule-type']) {
      const queryRuleType = route.query['rule-type'] as string
      if (['phonenumber', 'entity', 'text'].includes(queryRuleType)) {
        ruleType.value = queryRuleType as RuleTypeEnum
      }
    }

    if (isEditing.value) {
      loadRule().then(() => {
        validateTargetPhoneNumber(targetPhoneNumber.value)
      })
    }
  })
</script>

<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <div class="p-4">
      <div class="mx-auto max-w-4xl">
        <div class="mb-2 flex items-center gap-2">
          <button
            class="flex items-center gap-1 text-gray-500 hover:text-gray-700"
            @click="goBack"
          >
            <ArrowLeftIcon class="h-5 w-5" />
            <span class="text-sm">{{ t('backToRulesList') }}</span>
          </button>
        </div>
        <h1 class="text-xl font-semibold text-gray-900">
          {{ isEditing ? t('editRule') : t('newRule') }}
        </h1>
      </div>
    </div>

    <!-- Content -->
    <div class="mx-auto p-4">
      <!-- Step 1: Rule Configuration -->
      <div v-if="currentStep === 1">
        <Card class="mb-4">
          <CardContent class="space-y-4 p-4">
            <div class="mb-4">
              <h2 class="mb-1 text-lg font-medium text-gray-900">
                {{ t('setCallForwardingRule') }}
              </h2>
              <p class="text-sm text-gray-600">{{ t('ensureImportantCallers') }}</p>
            </div>

            <!-- Rule Title -->
            <div class="mb-6">
              <label class="mb-2 block cursor-default text-sm font-medium text-gray-900">
                {{ t('ruleTitle') }}
              </label>
              <Input
                v-model="ruleTitle"
                :placeholder="t('enterRuleTitle')"
                data-cy="PhoneForwardingRuleEditTitle"
              />
            </div>

            <!-- Phone Number Selection -->
            <div class="mb-6">
              <label class="mb-2 block cursor-default text-sm font-medium text-gray-900">
                {{ t('applyToPhoneNumbers') }}
              </label>
              <div
                class="w-[50%] rounded-lg border border-gray-200 bg-white p-4"
                :class="{ 'border-red-500': phoneNumberSelectionError }"
              >
                <div
                  v-if="loadingPhoneNumbers"
                  class="flex items-center justify-center py-4"
                >
                  <div
                    class="h-6 w-6 animate-spin rounded-full border-2 border-purple-600 border-t-transparent"
                  ></div>
                  <span class="ml-2 text-sm text-gray-600">{{ t('loadingPhoneNumbers') }}</span>
                </div>
                <div
                  v-else-if="availablePhoneNumbers.length === 0"
                  class="py-4 text-center text-sm text-gray-500"
                >
                  {{ t('noPhoneNumbersAvailable') }}
                </div>
                <div
                  v-else
                  class="space-y-2"
                >
                  <div
                    v-for="phoneNumber in availablePhoneNumbers"
                    :key="phoneNumber.uuid"
                    class="flex items-center justify-start gap-x-4 border-b border-gray-100 p-2 last:border-b-0"
                  >
                    <input
                      :id="`phone-${phoneNumber.uuid || 'unknown'}`"
                      class="h-5 w-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      type="checkbox"
                      :checked="
                        phoneNumber.uuid
                          ? selectedPhoneNumberUuids.includes(phoneNumber.uuid)
                          : false
                      "
                      @change="phoneNumber.uuid ? togglePhoneNumber(phoneNumber.uuid) : null"
                    />
                    <div class="flex flex-1 items-center justify-between">
                      <div class="flex flex-col">
                        <span class="text-sm font-medium text-gray-900">
                          {{ phoneNumber.phone_number }}
                        </span>
                        <span
                          v-if="phoneNumber.description"
                          class="text-xs text-gray-500"
                        >
                          {{ phoneNumber.description }}
                        </span>
                      </div>
                      <Badge
                        :variant="phoneNumber.active ? 'default' : 'secondary'"
                        class="text-xs"
                      >
                        {{ phoneNumber.active ? t('active') : t('inactive') }}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
              <div class="relative h-6">
                <p
                  v-if="phoneNumberSelectionError"
                  class="absolute left-0 top-0 pt-1 text-sm text-red-600"
                  data-cy="PhoneForwardingRuleEditPhoneSelectionError"
                >
                  {{ phoneNumberSelectionError }}
                </p>
              </div>
            </div>

            <!-- Rule Type Selection -->
            <div class="mb-6">
              <h3 class="mb-3 text-sm font-medium text-gray-900">{{ t('selectCondition') }}</h3>
              <Tabs
                v-model="ruleType"
                class="w-full"
              >
                <TabsList class="grid w-full grid-cols-3 gap-3 bg-gray-100">
                  <TabsTrigger value="phonenumber">
                    {{ t('basedOnPhoneNumbers') }}
                  </TabsTrigger>
                  <TabsTrigger value="entity">
                    {{ t('basedOnEntityTypes') }}
                  </TabsTrigger>
                  <TabsTrigger value="text">
                    {{ t('basedOnTextContext') }}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <!-- Rule Configuration -->
            <div class="space-y-6 rounded-lg bg-gray-100 p-4">
              <!-- Phone Numbers Configuration -->
              <div
                v-if="ruleType === 'phonenumber'"
                class="w-[50%] space-y-4"
              >
                <div>
                  <label class="mb-2 block cursor-default text-sm font-medium text-gray-900">
                    {{ t('whenCallComesFrom') }}
                  </label>
                  <div class="space-y-2">
                    <div
                      v-for="(_, index) in phoneNumbers"
                      :key="index"
                      class="flex items-center gap-2"
                    >
                      <Input
                        :id="`phone-number-${index}`"
                        v-model="phoneNumbers[index]"
                        :name="`phone-number-${index}`"
                        readonly
                        class="flex-1 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                        data-cy="PhoneForwardingRuleEditPhoneNumber"
                      />
                      <Button
                        variant="secondary"
                        data-cy="PhoneForwardingRuleEditRemoveNumber"
                        @click="removePhoneNumber(index)"
                      >
                        <TrashIcon class="size-4" />
                      </Button>
                    </div>
                    <div class="flex gap-2">
                      <div class="flex-1">
                        <Input
                          v-model="newPhoneNumber"
                          :class="[
                            'flex-1',
                            phoneNumberError
                              ? 'border-red-500 focus:border-red-500 focus-visible:ring-0 focus-visible:ring-offset-0'
                              : ''
                          ]"
                          data-cy="PhoneForwardingRuleEditNewNumber"
                          @input="validateFromPhoneNumber(newPhoneNumber)"
                          @blur="validateFromPhoneNumber(newPhoneNumber)"
                        />
                        <p
                          v-if="phoneNumberError"
                          class="mt-1 text-sm text-red-600"
                          data-cy="PhoneForwardingRuleEditPhoneNumberError"
                        >
                          {{ phoneNumberError }}
                        </p>
                      </div>
                      <Button
                        variant="secondary"
                        :disabled="!isPhoneNumberValid"
                        data-cy="PhoneForwardingRuleEditAddNumber"
                        @click="addPhoneNumber"
                      >
                        <PlusIcon class="size-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Entity Types Configuration -->
              <div
                v-if="ruleType === 'entity'"
                class="w-[50%] space-y-4"
              >
                <div>
                  <label class="mb-2 block text-sm font-medium text-gray-700">
                    {{ t('whenCallerIdentifiesAs') }}
                  </label>
                  <div class="space-y-2">
                    <div
                      v-for="entityValue in selectedEntities"
                      :key="entityValue"
                      class="flex items-center gap-2"
                    >
                      <Input
                        :model-value="getEntityTitle(entityValue)"
                        readonly
                        class="flex-1 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                        data-cy="PhoneForwardingRuleEditEntityInput"
                      />
                      <Button
                        variant="secondary"
                        data-cy="PhoneForwardingRuleEditRemoveEntity"
                        @click="removeEntity(entityValue)"
                      >
                        <TrashIcon class="h-4 w-4" />
                      </Button>
                    </div>
                    <div class="flex gap-2">
                      <SelectField
                        v-model="newEntitySelection"
                        name="newEntitySelection"
                        :options="
                          availableEntities.filter((e) => !selectedEntities.includes(e.value))
                        "
                        :select-option-text="t('selectEntity')"
                        class="flex-1"
                      />
                      <Button
                        variant="secondary"
                        :disabled="!newEntitySelection"
                        data-cy="PhoneForwardingRuleEditAddEntity"
                        @click="addEntity"
                      >
                        <PlusIcon class="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Text Context Configuration -->
              <div
                v-if="ruleType === 'text'"
                class="space-y-4"
              >
                <div>
                  <label class="mb-2 block text-sm font-medium text-gray-700">
                    {{ t('whenConditionIsMet') }}
                  </label>
                  <Textarea
                    v-model="textContext"
                    :placeholder="t('enterKeywordsOrPhrases')"
                    rows="3"
                    data-cy="PhoneForwardingRuleEditTextContext"
                  />
                </div>
              </div>

              <!-- Forwarding Target -->
              <div class="flex items-start gap-4">
                <ArrowLeftIcon class="mt-2 h-5 w-5 rotate-180 text-gray-400" />
                <label class="mt-1 cursor-default text-sm font-medium text-gray-700">
                  {{ t('itMustBeForwardedTo') }}
                </label>
                <div class="flex flex-col">
                  <Input
                    v-model="targetPhoneNumber"
                    :class="[
                      'w-64',
                      targetPhoneNumberError
                        ? 'border-red-500 focus:border-red-500 focus-visible:ring-0 focus-visible:ring-offset-0'
                        : ''
                    ]"
                    data-cy="PhoneForwardingRuleEditTargetNumber"
                    @input="validateTargetPhoneNumber(targetPhoneNumber)"
                    @blur="validateTargetPhoneNumber(targetPhoneNumber)"
                  />
                  <p
                    v-if="targetPhoneNumberError"
                    class="mt-1 text-sm text-red-600"
                    data-cy="PhoneForwardingRuleEditTargetNumberError"
                  >
                    {{ targetPhoneNumberError }}
                  </p>
                </div>
              </div>
            </div>
            <!-- Forwarding Message -->
            <div>
              <label class="mb-2 block cursor-default text-sm font-medium text-gray-900">
                {{ t('messageBeforeForwarding') }}
              </label>
              <TextAreaField
                v-model="forwardingMessage"
                name="forwardingMessage"
                rows="2"
                height-full
                data-cy="PhoneForwardingRuleEditMessage"
              />
            </div>
            <!-- Action Buttons -->
            <div class="mt-4 flex justify-end gap-2">
              <Button
                variant="secondary"
                data-cy="PhoneForwardingRuleEditCancel"
                @click="goBack"
              >
                {{ t('cancel') }}
              </Button>
              <Button
                :disabled="!isFormValid"
                data-cy="PhoneForwardingRuleEditNext"
                @click="goNext"
              >
                {{ t('next') }}
                <ChevronRightIcon class="ml-1 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <!-- Step 2: Review -->
      <div v-if="currentStep === 2">
        <Card class="mb-4">
          <CardContent class="p-4">
            <div class="mb-4">
              <h2 class="mb-1 text-lg font-medium text-gray-900">
                {{ t('reviewCallForwardingRule') }}
              </h2>
              <p class="text-sm text-gray-600">{{ t('pleaseCheckDetails') }}</p>
            </div>

            <!-- Rule Card -->
            <Card class="border border-gray-200">
              <CardContent class="p-3">
                <div class="mb-3 flex items-center justify-between">
                  <h3 class="text-base font-medium text-gray-900">
                    {{ getAutoTitle() }}
                  </h3>

                  <div class="flex items-center gap-3">
                    <div
                      class="flex items-center gap-2 rounded-lg border border-purple-200 bg-purple-50 px-2 py-1"
                    >
                      <Switch
                        :checked="isActive"
                        data-cy="PhoneForwardingRuleEditActiveToggle"
                        @update:checked="(checked) => toggleRuleStatus(checked)"
                      />
                      <span class="text-xs font-medium text-purple-800">{{ t('active') }}</span>
                    </div>
                  </div>
                </div>

                <!-- Rule Content -->
                <div class="mb-3 rounded-lg bg-gray-50 p-3">
                  <div class="mb-3 flex items-start gap-2">
                    <span class="text-sm font-medium text-gray-700">
                      {{ getConditionLabel }}
                    </span>
                    <!-- Show phone numbers for phone number rule type -->
                    <div
                      v-if="ruleType === 'phonenumber'"
                      class="flex flex-wrap gap-1"
                    >
                      <Badge
                        v-for="phoneNumber in phoneNumbers"
                        :key="phoneNumber"
                        variant="secondary"
                        class="text-xs"
                      >
                        {{ phoneNumber }}
                      </Badge>
                    </div>
                    <!-- Show entities for entity rule type -->
                    <div
                      v-if="ruleType === 'entity'"
                      class="flex flex-wrap gap-1"
                    >
                      <Badge
                        v-for="entityValue in selectedEntities"
                        :key="entityValue"
                        variant="secondary"
                        class="text-xs"
                      >
                        {{ getEntityTitle(entityValue) }}
                      </Badge>
                    </div>
                    <!-- Show text context for text rule type -->
                    <div
                      v-if="ruleType === 'text'"
                      class="flex flex-wrap gap-1"
                    >
                      <Badge
                        v-if="textContext"
                        variant="secondary"
                        class="text-xs"
                      >
                        {{ textContext }}
                      </Badge>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <ArrowLeftIcon class="h-4 w-4 rotate-180 text-gray-400" />
                    <span class="text-sm font-medium text-gray-700">
                      {{ t('itMustBeForwardedTo') }} {{ targetPhoneNumber }}
                    </span>
                  </div>
                </div>

                <!-- Forwarding Message -->
                <div>
                  <label class="mb-2 block text-sm font-medium text-gray-900">
                    {{ t('messageBeforeForwarding') }}
                  </label>
                  <div class="rounded-lg bg-gray-50 p-3">
                    <p
                      v-if="forwardingMessage"
                      class="text-sm font-medium text-gray-700"
                    >
                      {{ forwardingMessage }}
                    </p>
                    <p
                      v-else
                      class="text-sm text-gray-500"
                    >
                      {{ t('noForwardingMessage') }}
                    </p>
                  </div>
                </div>

                <!-- Applied Phone Numbers -->
                <div v-if="selectedPhoneNumberUuids.length > 0">
                  <label class="mb-2 block text-sm font-medium text-gray-900">
                    {{ t('appliedToPhoneNumbers') }}
                  </label>
                  <div class="rounded-lg bg-gray-50 p-3">
                    <div class="flex flex-wrap gap-1">
                      <Badge
                        v-for="phoneNumberUuid in selectedPhoneNumberUuids"
                        :key="phoneNumberUuid"
                        variant="secondary"
                        class="text-xs"
                      >
                        {{ getPhoneNumberDisplay(phoneNumberUuid) }}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <!-- Action Buttons -->
            <div class="mt-4 flex justify-end gap-2">
              <Button
                variant="secondary"
                data-cy="PhoneForwardingRuleEditBack"
                @click="goBack"
              >
                <ArrowLeftIcon class="mr-1 h-4 w-4" />
                {{ t('back') }}
              </Button>
              <Button
                data-cy="PhoneForwardingRuleEditSave"
                @click="saveRule"
              >
                {{ t('save') }}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  </div>
</template>
